import { FungibleToken } from "../models/financial/fungible_token.js";
import { FungibleTokenAmount } from "../models/financial/fungible_token_amount.js";
import { NonFungibleToken } from "../models/financial/nonfungible_token.js";
import { SemiFungibleToken } from "../models/financial/semifungible_token.js";
import { CurrencyAmount } from "../models/financial/currency_amount.js";
import { Account } from "../models/account.js";
import { USD_CURRENCY, ZERO_ADDRESS } from "../constants.js";
import { notEmpty } from "../utils/utils.js";
export class State {
    constructor(state) {
        this.accountAddressToTokens = new Map();
        state.forEach(({ fungibleTokenAmounts, nonFungibleTokens, semiFungibleTokens, accountAddress, neededTokenAllowances }) => {
            // create a map of tokenIdentifier -> spender -> amount
            const spenders = neededTokenAllowances.reduce((acc, allowance) => {
                const { spender, token, amount, platformId } = allowance;
                const tokenIdentifier = FungibleToken.identifier(platformId, token);
                if (!acc.has(tokenIdentifier)) {
                    acc.set(tokenIdentifier, new Map());
                }
                acc.get(tokenIdentifier).set(spender, amount);
                return acc;
            }, new Map());
            const fts = fungibleTokenAmounts.reduce((acc, amount) => {
                const spenderToAllowances = spenders.get(amount.token.identifier()) ?? new Map();
                acc.set(amount.token.identifier(), { amount, spenderToAllowances });
                return acc;
            }, new Map());
            const nfts = nonFungibleTokens.reduce((acc, token) => {
                acc.set(token.identifier(), token);
                return acc;
            }, new Map());
            const sfts = semiFungibleTokens.reduce((acc, token) => {
                acc.set(token.identifier(), token);
                return acc;
            }, new Map());
            this.accountAddressToTokens.set(accountAddress, {
                fungibleTokens: fts,
                nonFungibleTokens: nfts,
                semiFungibleTokens: sfts,
            });
        });
    }
    static fromFungibleTokenAmounts(accountAddress, fungibleTokenAmounts, neededTokenAllowances) {
        return new State([
            {
                accountAddress,
                fungibleTokenAmounts,
                nonFungibleTokens: [],
                semiFungibleTokens: [],
                neededTokenAllowances,
            },
        ]);
    }
    static fromTokenAndAmount(accountAddress, token, amount) {
        if (!token || !amount) {
            return undefined;
        }
        const fungibleTokenAmount = FungibleTokenAmount.fromRawAmount(token, amount);
        return State.fromFungibleTokenAmounts(accountAddress, [fungibleTokenAmount], []);
    }
    getFungibleTokens(accountAddress, platformId) {
        const accountState = this.accountAddressToTokens.get(Account.key(accountAddress));
        if (!accountState) {
            return [];
        }
        return Array.from(accountState.fungibleTokens.values())
            .filter((tokenAmount) => {
            return BigInt(tokenAmount.amount.token.platformId) == BigInt(platformId);
        })
            .map((tokenState) => tokenState.amount);
    }
    getFungibleTokenState(accountAddress) {
        const accountState = this.accountAddressToTokens.get(Account.key(accountAddress));
        if (!accountState) {
            return [];
        }
        return Array.from(accountState.fungibleTokens.values());
    }
    async aggregateFiatValueOfState() {
        const allFungibleTokens = await Promise.all(Array.from(this.accountAddressToTokens.values())
            .map((state) => Array.from(state.fungibleTokens.values()))
            .flat()
            .map(async (tokenAmount) => tokenAmount.amount.toTokenCurrencyValue()));
        return allFungibleTokens.filter(notEmpty).reduce((acc, curr) => {
            return acc.add(curr);
        }, CurrencyAmount.fromRawAmount(USD_CURRENCY, 0));
    }
    getNonFungibleTokens(accountAddress, platformId) {
        const accountState = this.accountAddressToTokens.get(Account.key(accountAddress));
        if (!accountState) {
            return [];
        }
        return Array.from(accountState.nonFungibleTokens.values()).filter((token) => {
            return BigInt(token.platformId) == BigInt(platformId);
        });
    }
    getSemiFungibleTokens(accountAddress, platformId) {
        const accountState = this.accountAddressToTokens.get(Account.key(accountAddress));
        if (!accountState) {
            return [];
        }
        return Array.from(accountState.semiFungibleTokens.values()).filter((token) => {
            return BigInt(token.platformId) == BigInt(platformId);
        });
    }
    addTokens(fungibleTokenAmounts, accountAddress, override) {
        const currentAccountState = this.accountAddressToTokens.get(accountAddress);
        if (!currentAccountState) {
            const fts = fungibleTokenAmounts.reduce((acc, amount) => {
                acc.set(amount.amount.token.identifier(), amount);
                return acc;
            }, new Map());
            this.accountAddressToTokens.set(accountAddress, {
                fungibleTokens: fts,
                nonFungibleTokens: new Map(),
                semiFungibleTokens: new Map(),
            });
            return this;
        }
        fungibleTokenAmounts.forEach((amount) => {
            const identifier = amount.amount.token.identifier();
            const currentAmount = currentAccountState.fungibleTokens.get(identifier);
            if (!currentAmount || override) {
                currentAccountState.fungibleTokens.set(identifier, amount);
            }
            else {
                // Merge spenderToAllowances
                amount.spenderToAllowances.forEach((allowance, spender) => {
                    const currentAllowance = currentAmount.spenderToAllowances.get(spender) || BigInt(0);
                    currentAmount.spenderToAllowances.set(spender, currentAllowance + allowance);
                });
                currentAccountState.fungibleTokens.set(identifier, {
                    amount: currentAmount.amount.add(amount.amount),
                    spenderToAllowances: currentAmount.spenderToAllowances,
                });
            }
        });
        return this;
    }
    addNonFungibleTokens(nonFungibleTokens, accountAddress, override) {
        const currentAccountState = this.accountAddressToTokens.get(accountAddress);
        if (!currentAccountState) {
            const nfts = nonFungibleTokens.reduce((acc, token) => {
                acc.set(token.identifier(), token);
                return acc;
            }, new Map());
            this.accountAddressToTokens.set(accountAddress, {
                fungibleTokens: new Map(),
                nonFungibleTokens: nfts,
                semiFungibleTokens: new Map(),
            });
            return this;
        }
        nonFungibleTokens.forEach((token) => {
            const identifier = token.identifier();
            if (!currentAccountState.nonFungibleTokens.has(identifier) || override) {
                currentAccountState.nonFungibleTokens.set(identifier, token);
            }
        });
        return this;
    }
    addSemiFungibleTokens(semiFungibleTokens, accountAddress, override) {
        const currentAccountState = this.accountAddressToTokens.get(accountAddress);
        if (!currentAccountState) {
            const sfts = semiFungibleTokens.reduce((acc, token) => {
                acc.set(token.identifier(), token);
                return acc;
            }, new Map());
            this.accountAddressToTokens.set(accountAddress, {
                fungibleTokens: new Map(),
                nonFungibleTokens: new Map(),
                semiFungibleTokens: sfts,
            });
            return this;
        }
        semiFungibleTokens.forEach((token) => {
            const identifier = token.identifier();
            if (!currentAccountState.semiFungibleTokens.has(identifier) || override) {
                currentAccountState.semiFungibleTokens.set(identifier, token);
            }
        });
        return this;
    }
    addState(other, override) {
        Array.from(other.accountAddressToTokens.keys()).forEach((accountAddress) => {
            const otherAccountTokens = other.accountAddressToTokens.get(accountAddress);
            this.addTokens(Array.from(otherAccountTokens.fungibleTokens.values()), accountAddress, override);
            this.addNonFungibleTokens(Array.from(otherAccountTokens.nonFungibleTokens.values()), accountAddress, override);
            this.addSemiFungibleTokens(Array.from(otherAccountTokens.semiFungibleTokens.values()), accountAddress, override);
        });
        return this;
    }
    deductState(other) {
        Array.from(other.accountAddressToTokens.keys()).forEach((accountKey) => {
            const currentAccountState = this.accountAddressToTokens.get(accountKey);
            if (!currentAccountState) {
                return;
            }
            // Handle fungible tokens
            other.accountAddressToTokens.get(accountKey).fungibleTokens.forEach((amount) => {
                const identifier = amount.amount.token.identifier();
                const currentAmount = currentAccountState.fungibleTokens.get(identifier);
                if (currentAmount?.amount.toRawAmount() > amount?.amount.toRawAmount()) {
                    currentAccountState.fungibleTokens.set(identifier, {
                        amount: currentAmount.amount.subtract(amount.amount),
                        spenderToAllowances: currentAmount.spenderToAllowances,
                    });
                }
                else if (currentAmount) {
                    if (currentAmount?.spenderToAllowances?.size == 0) {
                        currentAccountState.fungibleTokens.delete(identifier);
                    }
                    else {
                        currentAccountState.fungibleTokens.set(identifier, {
                            amount: amount.amount.cloneWithAmount(BigInt(0)),
                            spenderToAllowances: currentAmount.spenderToAllowances,
                        });
                    }
                }
            });
            // Handle non-fungible tokens
            other.accountAddressToTokens.get(accountKey).nonFungibleTokens.forEach((token) => {
                const identifier = token.identifier();
                if (currentAccountState.nonFungibleTokens.has(identifier)) {
                    currentAccountState.nonFungibleTokens.delete(identifier);
                }
            });
            // Handle semi-fungible tokens
            other.accountAddressToTokens.get(accountKey).semiFungibleTokens.forEach((token) => {
                const identifier = token.identifier();
                if (currentAccountState.semiFungibleTokens.has(identifier)) {
                    currentAccountState.semiFungibleTokens.delete(identifier);
                }
            });
        });
        this.accountAddressToTokens.forEach((state, accountKey) => {
            state.fungibleTokens.forEach((amount, identifier) => {
                if (amount.amount.toRawAmount() == BigInt(0)) {
                    state.fungibleTokens.delete(identifier);
                }
            });
            if (state.fungibleTokens.size == 0 && state.nonFungibleTokens.size == 0 && state.semiFungibleTokens.size == 0) {
                this.accountAddressToTokens.delete(accountKey);
            }
        });
        return this;
    }
    equals(other) {
        if (!other) {
            return false;
        }
        return (this.accountAddressToTokens.size == other.accountAddressToTokens.size &&
            Array.from(this.accountAddressToTokens.keys()).every((key) => {
                const thisState = this.accountAddressToTokens.get(key);
                const otherState = other.accountAddressToTokens.get(key);
                return (thisState.fungibleTokens.size == otherState.fungibleTokens.size &&
                    thisState.nonFungibleTokens.size == otherState.nonFungibleTokens.size &&
                    thisState.semiFungibleTokens.size == otherState.semiFungibleTokens.size &&
                    Array.from(thisState.fungibleTokens.keys()).every((tokenKey) => {
                        const thisToken = thisState.fungibleTokens.get(tokenKey);
                        const otherToken = otherState.fungibleTokens.get(tokenKey);
                        return (thisToken?.amount?.equals(otherToken?.amount) &&
                            thisToken?.spenderToAllowances?.size == otherToken?.spenderToAllowances?.size &&
                            Array.from(thisToken?.spenderToAllowances?.entries()).every(([spender, amount]) => {
                                return otherToken?.spenderToAllowances?.get(spender) == amount;
                            }));
                    }) &&
                    Array.from(thisState.nonFungibleTokens.keys()).every((tokenKey) => {
                        const thisToken = thisState.nonFungibleTokens.get(tokenKey);
                        const otherToken = otherState.nonFungibleTokens.get(tokenKey);
                        return thisToken.equals(otherToken);
                    }) &&
                    Array.from(thisState.semiFungibleTokens.keys()).every((tokenKey) => {
                        const thisToken = thisState.semiFungibleTokens.get(tokenKey);
                        const otherToken = otherState.semiFungibleTokens.get(tokenKey);
                        return thisToken.equals(otherToken);
                    }));
            }));
    }
    addStates(states) {
        states.forEach((state) => this.addState(state, false));
        return this;
    }
    static mergeStates(states) {
        return states.reduce((acc, state) => acc.addState(state, false), new State([]));
    }
    static netInputAndOutputStates(states, finalTransactionInputState, finalTransactionOutputState) {
        // quotes combined input states
        const combinedInputState = states.reduce((acc, state) => {
            if (state?.inputState) {
                acc.addState(state.inputState, false);
            }
            return acc;
        }, finalTransactionInputState ?? new State([]));
        // quotes combined output states
        const combinedOutputState = states.reduce((acc, state) => {
            if (state?.outputState) {
                acc.addState(state.outputState, false);
            }
            return acc;
        }, finalTransactionOutputState ?? new State([]));
        // net input state = all inputs - all outputs (and drop the negative values)
        const netInputState = combinedInputState.deepCopy().deductState(combinedOutputState);
        // net output state = all outputs - all inputs (and drop the negative values)
        const netOutputState = combinedOutputState.deepCopy().deductState(combinedInputState);
        return { outputState: netOutputState, inputState: netInputState };
    }
    static toState(state) {
        if (!state) {
            return undefined;
        }
        // Handle old format (arrays of tokens without accounts)
        if (Array.isArray(state.fungibleTokenAmounts)) {
            const fungibleTokenAmounts = state.fungibleTokenAmounts.map((ft) => FungibleTokenAmount.toFungibleTokenAmount(ft));
            const nonFungibleTokens = state.nonFungibleTokenAmounts.map((nft) => NonFungibleToken.toNonFungibleToken(nft));
            const semiFungibleTokens = state.semiFungibleTokenAmounts.map((sft) => SemiFungibleToken.toSemiFungibleToken(sft));
            return new State([
                {
                    accountAddress: ZERO_ADDRESS,
                    fungibleTokenAmounts,
                    nonFungibleTokens,
                    semiFungibleTokens,
                    neededTokenAllowances: [],
                },
            ]);
        }
        else if (notEmpty(state?.[0]?.fungibleTokens)) {
            const stateArray = state.map((item) => {
                let neededTokenAllowances = [];
                const fungibleTokenAmounts = item.fungibleTokens.map((ft) => {
                    const amount = FungibleTokenAmount.toFungibleTokenAmount(ft.amount);
                    const extractedAllowances = (JSON.parse(ft.spenderToAllowances) ?? []).map((entry) => ({
                        spender: entry[0],
                        token: ft.amount.token.address,
                        amount: BigInt(entry[1]),
                        platformId: BigInt(ft.amount.token.platformId ?? ft.amount.token.chainId),
                    }));
                    neededTokenAllowances.push(...extractedAllowances);
                    return amount;
                });
                const nonFungibleTokens = item.nonFungibleTokenAmounts.map((nft) => NonFungibleToken.toNonFungibleToken(nft));
                const semiFungibleTokens = item.semiFungibleTokenAmounts.map((sft) => SemiFungibleToken.toSemiFungibleToken(sft));
                return {
                    accountAddress: item.accountAddress,
                    fungibleTokenAmounts,
                    nonFungibleTokens,
                    semiFungibleTokens,
                    neededTokenAllowances,
                };
            });
            return new State(stateArray);
        }
        // Handle the format without allowances
        const stateArray = state.map((item) => ({
            accountAddress: item.accountAddress,
            fungibleTokenAmounts: item.fungibleTokenAmounts.map((ft) => FungibleTokenAmount.toFungibleTokenAmount(ft)),
            nonFungibleTokens: item.nonFungibleTokenAmounts.map((nft) => NonFungibleToken.toNonFungibleToken(nft)),
            semiFungibleTokens: item.semiFungibleTokenAmounts.map((sft) => SemiFungibleToken.toSemiFungibleToken(sft)),
            neededTokenAllowances: item.neededTokenAllowances || [],
        }));
        return new State(stateArray);
    }
    // return State in the "@domains/orby/src/api/graphql/generated" but could not set type here because the generated code is not available for other domains
    toStateModel() {
        const fungibleTokenAmounts = Array.from(this.accountAddressToTokens.values())
            .map((state) => Array.from(state.fungibleTokens.values()).map((ft) => ft?.amount.toFungibleTokenAmountModel()))
            ?.flat();
        const nonFungibleTokenAmounts = Array.from(this.accountAddressToTokens.values())
            .map((state) => Array.from(state.nonFungibleTokens.values()).map((ft) => ft?.toNonFungibleTokenModel()))
            ?.flat();
        const semiFungibleTokenAmounts = Array.from(this.accountAddressToTokens.values())
            .map((state) => Array.from(state.semiFungibleTokens.values()).map((ft) => ft?.toSemiFungibleTokenModel()))
            ?.flat();
        return { fungibleTokenAmounts, nonFungibleTokenAmounts, semiFungibleTokenAmounts };
    }
    // return State in the "@domains/orby/src/api/graphql/generated" but could not set type here because the generated code is not available for other domains
    toAccountStateModel() {
        return Array.from(this.accountAddressToTokens.keys()).map((accountAddress) => {
            const tokens = this.accountAddressToTokens.get(accountAddress);
            return {
                accountAddress,
                fungibleTokens: Array.from(tokens.fungibleTokens.values()).map((ft) => {
                    return {
                        amount: ft?.amount?.toFungibleTokenAmountModel(),
                        spenderToAllowances: JSON.stringify(Array.from(ft?.spenderToAllowances.entries())),
                    };
                }),
                nonFungibleTokenAmounts: Array.from(tokens.nonFungibleTokens.values()).map((ft) => ft?.toNonFungibleTokenModel()),
                semiFungibleTokenAmounts: Array.from(tokens.semiFungibleTokens.values()).map((ft) => ft?.toSemiFungibleTokenModel()),
            };
        });
    }
    /**
     * Creates a deep copy of the current state
     * @returns A new State instance with copied tokens
     */
    deepCopy() {
        const stateArray = Array.from(this.accountAddressToTokens.entries()).map(([accountAddress, state]) => {
            // Create new instances of FungibleTokenAmount with new amounts
            const fungibleTokenAmounts = Array.from(state.fungibleTokens.values()).map((ft) => FungibleTokenAmount.fromRawAmount(ft.amount.token, ft.amount.toRawAmount()));
            const neededTokenAllowances = Array.from(state.fungibleTokens.values()).flatMap((ft) => {
                return Array.from(ft.spenderToAllowances.entries()).map(([spender, amount]) => ({
                    spender,
                    token: ft.amount.token.address,
                    amount,
                    platformId: ft.amount.token.platformId,
                }));
            });
            const nonFungibleTokens = Array.from(state.nonFungibleTokens.values()).map((nft) => nft);
            const semiFungibleTokens = Array.from(state.semiFungibleTokens.values()).map((sft) => sft);
            return {
                accountAddress,
                fungibleTokenAmounts,
                nonFungibleTokens,
                semiFungibleTokens,
                neededTokenAllowances,
            };
        });
        return new State(stateArray);
    }
    /**
     * Creates a new State object containing only the accounts specified in the address list
     * @param addresses List of account addresses to include in the new state
     * @returns A new State instance containing only the specified accounts
     */
    filterByAddresses(addresses) {
        const stateArray = Array.from(this.accountAddressToTokens.entries())
            .filter(([accountAddress, _state]) => addresses?.includes(accountAddress))
            .map(([accountAddress, state]) => {
            const fungibleTokenAmounts = Array.from(state.fungibleTokens.values()).map((ft) => FungibleTokenAmount.fromRawAmount(ft.amount.token, ft.amount.toRawAmount()));
            const neededTokenAllowances = Array.from(state.fungibleTokens.values()).flatMap((ft) => {
                return Array.from(ft.spenderToAllowances.entries()).map(([spender, amount]) => ({
                    spender,
                    token: ft.amount.token.address,
                    amount,
                    platformId: ft.amount.token.platformId,
                }));
            });
            const nonFungibleTokens = Array.from(state.nonFungibleTokens.values());
            const semiFungibleTokens = Array.from(state.semiFungibleTokens.values());
            return {
                accountAddress,
                fungibleTokenAmounts,
                nonFungibleTokens,
                semiFungibleTokens,
                neededTokenAllowances,
            };
        });
        return new State(stateArray);
    }
}
